"use client";

import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import type { FaqItem } from "@/data/types";

interface FAQProps {
  items: FaqItem[];
  visibleCount?: number;
  title?: string;
  subtitle?: string;
}

export default function FAQ({
  items,
  visibleCount = 7,
  title,
  subtitle,
}: FAQProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [openItems, setOpenItems] = useState<Set<string>>(
    new Set(items.length > 0 ? [items[0].id] : [])
  );

  const visibleItems = items.slice(0, visibleCount);
  const hiddenItems = items.slice(visibleCount);

  const toggleItem = (id: string) => {
    setOpenItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const renderFaqItem = (item: FaqItem, isOpen: boolean) => (
    <div
      key={item.id}
      className="border-b border-white/5 last:border-0"
    >
      <button
        onClick={() => toggleItem(item.id)}
        className="flex w-full items-center justify-between py-4 text-left transition-colors hover:text-accent"
        aria-expanded={isOpen}
      >
        <span className="text-base font-medium text-text-main pr-4">
          {item.question}
        </span>
        {isOpen ? (
          <ChevronUp className="h-5 w-5 shrink-0 text-accent" />
        ) : (
          <ChevronDown className="h-5 w-5 shrink-0 text-text-muted" />
        )}
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${isOpen ? "max-h-[1000px] pb-4" : "max-h-0"
          }`}
      >
        <div
          className="text-text-muted font-body text-[15px] leading-relaxed"
          dangerouslySetInnerHTML={{ __html: item.answer }}
        />
      </div>
    </div>
  );

  return (
    <div className="w-full">
      {title && (
        <div className="mb-8 flex flex-col items-center justify-center text-center">
          <h2 className="w-full text-center font-heading text-[43px] font-bold leading-tight text-text-main">
            {title}
          </h2>
          {subtitle && (
            <p className="mt-1 text-center text-sm text-white/60 font-body">
              {subtitle}
            </p>
          )}
        </div>
      )}

      {/* ВИДИМЫЕ ВОПРОСЫ */}
      <div className="space-y-0 border-t border-white/5">
        {visibleItems.map((item) =>
          renderFaqItem(item, openItems.has(item.id))
        )}
      </div>

      {/* СКРЫТЫЕ ВОПРОСЫ ПОД АККОРДЕОНОМ */}
      {hiddenItems.length > 0 && (
        <>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="mt-6 flex w-full items-center justify-center gap-2 py-4 text-sm font-heading font-bold text-accent border border-accent/20 rounded-sm transition-colors hover:bg-accent/10"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="h-5 w-5" />
                Näita vähem küsimusi
              </>
            ) : (
              <>
                <ChevronDown className="h-5 w-5" />
                Näita rohkem küsimusi ({hiddenItems.length})
              </>
            )}
          </button>

          <div
            className={`overflow-hidden transition-all duration-500 ${isExpanded ? "max-h-[9999px] mt-4" : "max-h-0"
              }`}
          >
            <div className="space-y-0 border-t border-white/5">
              {hiddenItems.map((item) =>
                renderFaqItem(item, openItems.has(item.id))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}